<template>
    <div class="chatTray">
        <a class="drop-down-menu" v-on:click="toggleTray()">
            <i class="fa fa-bars"></i>
        </a>
        <div class="chat-wrapper">
            <div>
                <chatContacts v-if="historyOpen" class="side-nav" :user="user" :contacts="contacts" :person="person"></chatContacts>
            </div>
            <div class="chat-container" :disabled="!this.person">
                <div ref="chat" class="chat">
                    <div v-if="isEmpty" :class="backgroundMessage"><div>{{$t('str.NoMessages')}}</div></div>
                    <ul v-else class="chatList" name="messages" :style="messagesIEHackStyle">
                        <chatbox v-for="(messageData, index) in chatList" :key="index" :messageData="messageData" :person="getPerson(messageData.from)" :user="user" v-on:openPersonCard="openPersonCard($event)"></chatbox>
                        <chatbox v-for="(messageData, index) in messagesSending" :key="chatList.length + index" :messageData="messageData" :person="user" :user="user" v-on:openPersonCard="openPersonCard($event)"></chatbox>
                    </ul>
                </div>
                <div :class="chatSubmit">
                    <textarea v-model="chatText" v-on:keyup.enter="submitText" class="chat-text" :placeholder="placeholder" :disabled="!this.person"></textarea>
                    <img v-on:click="submitText" class="submit-button" src="../../assets/icons/enter.svg">    
                </div>
                <div class="chat-arrow-small"></div>
            </div>
        </div>
        <personcard v-if="this.viewPerson" :attendee="this.viewPerson" :user="user" v-on:cardClosed="personCardClosed"></personcard>
    </div>
</template>

<script>

import ChatBox from '@/components/chat/ChatBox';
import ChatContacts from '@/components/chat/ChatContacts';
import PersonCard from '@/components/PersonCard';
import NavigatorUtils from '../../javascript-classes/utils/navigator';
import {EventBus, LeadBus} from '../../router/index';

const POLL_TIMEOUT = 30000;

export default {
    components: {
        chatbox: ChatBox,
        chatContacts: ChatContacts,
        personcard: PersonCard
    },

    props: {
        presentation: {},
        peopleMap: {},
        chatList: {},
        contacts: {},
        user: {},
        person: {}
    },

    data () {
        return {
            chatText: '',
            viewPerson: undefined,
            historyOpen: window.screen.width > 450, // By default only open if there is enough space to show both
            pollTimeout: undefined,
            messagesIEHackStyle: {},
            messagesSending: [],
            lastMessagesSentCount: 0
        };
    },

    computed: {
        isEmpty() {
            return !this.chatList || !this.chatList.length;
        },

        placeholder() {
            if (this.person === undefined) {
                return this.$t('str.SelectAContactToChatWith');  
            }

            return this.$t('str.ChatWith', {
                firstname: this.person.firstName,
                lastname: this.person.lastName
            });
        },

        backgroundMessage() {
            return 'background-message ' + (this.historyOpen ? 'small' : '');
        },

        chatSubmit() {
            return 'chatSubmit ' + (this.historyOpen ? 'small' : '');
        }
    },

    mounted() {
        EventBus.$on('closeHistory', () => {
            this.historyOpen = false;
        });

        EventBus.$emit('getPeople', this.presentation.id); 
        EventBus.$emit('getContacts', this.user.id);

        this.showLastMessages();

        // Poll new messages
        this.pollTimeout = setInterval(() => {
            if (this.person) {
                EventBus.$emit('getMessages', this.person.id);
            }
        }, POLL_TIMEOUT);

        // IE hack
        if (NavigatorUtils.isIE()) {
            let updateHeight = () => {
                this.messagesIEHackStyle = {
                    height: (parseInt(this.$el.offsetHeight) - 60) + "px",
                    "overflow-y": "scroll"
                };
            };

            document.body.addEventListener("mselementresize", () => { updateHeight(); });
            updateHeight();
        }
    },

    beforeDestroy() {
        if (this.pollTimeout) {
            clearInterval(this.pollTimeout);
        }
    },

    watch: {
        chatList(list) {
            this.showLastMessages();

            // Remove the sending messages as we received confirmation
            // from the server that they were received.
            if (!list) {
                return;
            }

            let newMessagesSentCount = 0;

            for(let i = 0; i < list.length; ++i) {
                if (list[i].from === this.user.id) {
                    ++newMessagesSentCount;
                }
            };

            this.messagesSending.splice(0, newMessagesSentCount - this.lastMessagesSentCount);
            this.lastMessagesSentCount = newMessagesSentCount;
        }
    },

    methods: {
        toggleTray() {
            if (!this.user) {
                return;
            }

            EventBus.$emit('getContacts', this.user.id); 
            this.historyOpen = !this.historyOpen;
        },

        submitText() {
            if (!this.chatText || this.chatText === '' || !this.person || !this.user) {
                return;
            }

            let message = this.chatText.trim();

            this.messagesSending.push({
                from: this.user.id,
                message: message,
                timestamp: new Date()
            });

            EventBus.$emit('postMessage', message, this.person.id);
            this.chatText = '';

            setTimeout(() => {
                EventBus.$emit('getMessages', this.person.id);
            }, 100);

            EventBus.$emit('getContacts', this.user.id);
            LeadBus.$emit('recordLead', {
                type: 'SendMessage',
                data: {
                    messageSentTo: this.person.id
                }
            });
        },

        getPerson(personId) {   
            return this.peopleMap && this.peopleMap.get ? this.peopleMap.get(personId) : {};
        },

        openPersonCard(person) {
            this.viewPerson = person;
        },

        personCardClosed() {
            this.viewPerson = undefined;
        },

        showLastMessages() {
            if (this.$refs.chat === undefined) {
                return;
            }

            setTimeout(() => {
                this.$refs.chat.scrollTop = this.$refs.chat.scrollHeight;
            }, 100);  
        }
    }
};

// TODO: change sizing according to media
</script>

<style lang="scss">

$mobile-portrait-max-width: 480px;

.chatTray {
    position: absolute;
    top: 65px;
    bottom: 0;
    width: 100%;
    overflow: hidden;

    .drop-down-menu {
        position: fixed;
        margin-left: 40px;
        cursor: pointer;
        margin-top: -38px;
    }

    .chat-wrapper {
        display: table;
        width: 100%;
        height: 100%;

        & > div {
            display: table-cell;
            vertical-align: top;
            width: 1px;
            height: 100%;
        }

        .chat-container {
            width: 100%;
            position: relative;
            min-width: 290px;
        }

        .chat-container[disabled="disabled"] {
            $disable-color: #bdb9b9;

            .chatSubmit {
                background-color: $disable-color;

                .chat-text {
                    background-color: $disable-color;
                }

                .submit-button {
                    cursor: initial;
                }
            }

            .chat-arrow-small {
                border-right-color: $disable-color;
            }
        }
    }

    .side-nav {
        @media screen and (max-width: 450px) {
            width: 100%;
        }

        width: 100%;
        min-width: 320px;
        max-width: 450px;
        height: 100%;
        background-color: #000;
        overflow-x: hidden;
        overflow-y: auto;
        transition: 0.5s;
        border-right: 3px solid #d4451d;
    }



    .chat {
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        overflow-x: hidden;
        min-height: 58%;
        height: calc(100% - 60px);
        width: 100%;
    }

    .chatList {
        list-style-type: none;
        padding: 0px 10px 10px 10px;
        margin: 0;
        width: 100%;
    }

    .chatSubmit {
        position: absolute;
        left: 10px;
        bottom: 0;
        right: 22px;
        height: 55px;
        background-color: #fff;
        overflow: hidden;
        border-radius: 10px 10px 10px 10px;

        @media screen and (max-width: $mobile-portrait-max-width) {
            right: 10px;
        }
    }

    .chatSubmit.small {
        box-sizing: inherit;
    }

    .chat-text {
        height: 45px;
        width: calc(100% - 50px);
        color: #000;
        background-color: #fff;
        font-family: 'Poppins', sans-serif;
        font-size: 1em;
        line-height: 1.2em;
        padding: 13px 0 0 10px;
        outline: none;
        border: none;
        resize: none;
        overflow-y: auto; // Removes scrollbar on IE
    }

    .submit-button {
        width: 20px;
        height: 20px;
        line-height: 20px;
        position: absolute;
        bottom: 20px;
        right: 14px;
        z-index: 1;
        cursor: pointer;
        display: inline-block;
        overflow: hidden;
        user-select: none;
    }

    .chat-arrow-small {
        @media screen and (max-width: $mobile-portrait-max-width) {
            border: none;
        }

        position: absolute;
        border: solid transparent;
        border-right-color: #fff;
        content: '';
        height: 0;
        width: 0;
        right: -8px;
        bottom: 12px;
        border-width: 15px;
        transform: rotate(180deg);
    }
    
    .background-message {
        top: 0;
        width: 100%;
        height: 100%;
        display: table;
        position: absolute;
        font-size: 0.9em;
        font-family: "Poppins", "Helvetica Neue", Helvetica, Arial, sans-serif;

        & > div {
            display: table-cell;
            text-align: center;
            width: 100%;
            vertical-align: middle;
        }
    } 

    .background-message.small {
        font-weight: normal;
    } 
} 

::-webkit-scrollbar {
  width: 5px;
}

::-webkit-scrollbar-track {
  border-radius: 0;
  box-shadow: inset 0 0 1px rgba(0, 0, 0, 0.0);
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.0);
  border-radius: 0;
}

::-webkit-scrollbar-thumb:window-inactive {
  background: rgba(0, 0, 0, 0.0);
} 

</style>



// WEBPACK FOOTER //
// ChatTray.vue?002debdd